import { apiGetShopifyDiscountInfo } from 'lib/api';
import { SHOPIFY_FUNCTION_BOGO_CODES } from 'lib/constants/shopify-function-codes';
import { discountAppliesToProduct } from './discount-applies-to-product';

export type DiscountItems = {
  __typename?: string;
  allItems?: boolean;
  products?: { nodes: Array<{ id: string }> };
  collections?: {
    nodes: Array<{
      id: string;
      products?: { nodes: Array<{ id: string }> };
    }>;
  };
};

export type ShopifyDiscountValue = {
  percentage?: number;
  amount?: { amount: string };
};

export type PromoCodeDiscountInfo = {
  code: string;
  value: ShopifyDiscountValue;
  items?: DiscountItems;
  // Subscription orders the code discounts, first checkout included; 0 = all.
  recurringCycleLimit?: number | null;
} | null;

// A resolved code that applies to the product on screen.
export type ProductPromoCode = Pick<
  NonNullable<PromoCodeDiscountInfo>,
  'code' | 'value' | 'recurringCycleLimit'
>;

export type DiscountCodeLookup = {
  redeemable: boolean;
  info: PromoCodeDiscountInfo;
};

export type ResolvePromoCodeOptions = {
  // Anchors percentage-to-dollar conversion when comparing a percentage code
  // against a fixed-amount code. Callers pass the viewed product's price on a
  // PDP and the DS-01 price elsewhere.
  basePrice?: number;
  // Shopify product GIDs of the PDP being viewed. When set, site-side codes
  // that provably don't apply to any of them are dropped before comparison.
  viewedProductIds?: string[] | null;
  // Observed USD→market FX ratio for the market the cart charges in (1 for
  // USD). Fixed-amount codes are USD-denominated and Shopify converts them at
  // checkout, so their comparison value converts the same way.
  usdToMarketRate?: number;
};

// Extracts PromoCodeDiscountInfo from a Shopify discount API response
export const extractDiscountInfo = (
  code: string,
  response: any,
): PromoCodeDiscountInfo => {
  const codeDiscount =
    response?.data?.response?.codeDiscountNodeByCode?.codeDiscount;
  // App codes compute their value in a Shopify Function, so only the code is known.
  if (codeDiscount?.__typename === 'DiscountCodeApp')
    return { code, value: {} };
  if (!codeDiscount?.customerGets) return null;
  return {
    code,
    value: codeDiscount.customerGets.value,
    items: codeDiscount.customerGets.items,
    recurringCycleLimit: codeDiscount.recurringCycleLimit,
  };
};

const lookupCache = new Map<string, DiscountCodeLookup>();

// Test-only: lookups memoize per session, so suites must reset between tests.
export const clearDiscountLookupCache = (): void => {
  lookupCache.clear();
};

// 'info' only available for basic discounts, not BXGY/free-shipping types
export const lookupDiscountCode = async (
  code: string,
): Promise<DiscountCodeLookup> => {
  const cached = lookupCache.get(code.toUpperCase());
  if (cached) {
    return cached;
  }

  const response = await apiGetShopifyDiscountInfo({ discountCode: code });
  if (response?.error || !response?.data?.response) {
    console.log(response);
    return { redeemable: true, info: null };
  }

  const node = response.data.response.codeDiscountNodeByCode;
  // A missing status (unrecognized discount type) stays optimistic.
  const status = node?.codeDiscount?.status;
  const result = {
    redeemable: !!node && (!status || status === 'ACTIVE'),
    info: extractDiscountInfo(code, response),
  };
  if (result.redeemable) {
    lookupCache.set(code.toUpperCase(), result);
  }
  return result;
};

// Market-currency value of a discount: percentages against the (localized)
// base price, USD fixed amounts converted by the market rate. A code with no
// known value (failed lookup, BXGY/free-shipping) counts as 0.
const getAbsoluteDiscountValue = (
  discountValue: ShopifyDiscountValue | null | undefined,
  basePrice: number,
  usdToMarketRate: number,
): number => {
  if (!discountValue) return 0;

  if (discountValue.percentage) {
    return Number((basePrice * discountValue.percentage).toFixed(2));
  }

  if (discountValue.amount?.amount) {
    return parseFloat(discountValue.amount.amount) * usdToMarketRate;
  }

  return 0;
};

type Candidate = { code: string; info: PromoCodeDiscountInfo; value: number };

// Compares the value of a query param, mini-sale, and local storage coupon code
// that are 'redeemable'.
// query param code takes precedence, then site-wide sale code, then local storage code
// local storage code is only considered if it applies to the product being viewed (ie user is on a pdp).
// mini-sale code only considered if it is not excluded by mini-sale config (ie not on an excluded route)
export const resolveBestPromoCode = async (
  promoCodeFromUrl: string | null,
  siteCodes: Array<string | null | undefined>,
  options: ResolvePromoCodeOptions = {},
): Promise<{ code: string; info: PromoCodeDiscountInfo } | null> => {
  const { basePrice = 0, viewedProductIds, usdToMarketRate = 1 } = options;

  // Dedupe case-insensitively, keeping the first occurrence — a code present
  // in both the URL and a site-side source counts as the URL code.
  const codes = [promoCodeFromUrl, ...siteCodes]
    .filter((code): code is string => !!code)
    .filter(
      (code, index, all) =>
        all.findIndex((c) => c.toUpperCase() === code.toUpperCase()) === index,
    );

  if (codes.length === 0) {
    return null;
  }

  const lookups = await Promise.all(codes.map(lookupDiscountCode));
  const candidates: Candidate[] = codes
    .map((code, index) => ({ code, ...lookups[index] }))
    .filter((candidate) => candidate.redeemable)
    .map(({ code, info }) => ({
      code,
      info,
      value: getAbsoluteDiscountValue(info?.value, basePrice, usdToMarketRate),
    }));

  // BOGO codes are app discounts with no reportable value, so they outrank
  // every other code instead of being scored as $0. A failed lookup (no info)
  // doesn't count, so an unverified BOGO can't displace a live code.
  const bogoCandidate = candidates.find(
    (candidate) =>
      candidate.info != null &&
      candidate.code.toUpperCase() in SHOPIFY_FUNCTION_BOGO_CODES,
  );
  if (bogoCandidate) {
    return { code: bogoCandidate.code, info: bogoCandidate.info };
  }

  const urlCandidate =
    candidates.find(
      (candidate) =>
        candidate.code.toUpperCase() === promoCodeFromUrl?.toUpperCase(),
    ) ?? null;

  // A site-side code is only dropped when its product entitlements are known
  // and exclude the viewed product — unknown entitlements can't be proven
  // inapplicable (and their unknown value of $0 never beats a URL code).
  const appliesToViewedProduct = (candidate: Candidate): boolean =>
    !viewedProductIds?.length ||
    !candidate.info?.items ||
    viewedProductIds.some((id) =>
      discountAppliesToProduct(id, candidate.info?.items),
    );

  // Candidates keep their input order (sale code before localStorage code),
  // so on equal value the sale code wins among the site-side codes.
  const bestSiteCandidate = candidates
    .filter(
      (candidate) =>
        candidate !== urlCandidate && appliesToViewedProduct(candidate),
    )
    .reduce<Candidate | null>(
      (best, candidate) =>
        !best || candidate.value > best.value ? candidate : best,
      null,
    );

  // Ties go to the URL code — the user's latest intent.
  const urlWins =
    !!urlCandidate &&
    (!bestSiteCandidate || urlCandidate.value >= bestSiteCandidate.value);
  const winner = urlWins ? urlCandidate : bestSiteCandidate;
  return winner ? { code: winner.code, info: winner.info } : null;
};
